import { IWorkspaceSettings } from "../types/WorkspaceSettings";

type WorkspaceSettingsLike = IWorkspaceSettings | null | undefined;

/**
 * True when a location id refers to the workspace's default pickup location.
 *
 * Internal orders clone saved locations with a unique id in the form
 * `<originalId>_<timestamp>_<rand>` (see RouteSelection), so we match both the
 * exact id and that prefixed variant.
 */
export const isDefaultPickupLocationId = (
  locationId: string | undefined | null,
  settings: WorkspaceSettingsLike,
): boolean => {
  const defaultId = settings?.defaultPickupLocationId;
  if (!defaultId || !locationId) return false;
  return locationId === defaultId || locationId.startsWith(`${defaultId}_`);
};

/**
 * True when the given stop is a pickup at the workspace's default pickup
 * location. Ignores the `hidePickupStops` toggle; use {@link isHiddenDefaultPickup}
 * when you also need the toggle applied.
 */
export const isDefaultLocationPickup = (
  stop: { isPickup?: boolean; location?: { id?: string } | null } | null | undefined,
  settings: WorkspaceSettingsLike,
): boolean => {
  if (!stop?.isPickup) return false;
  return isDefaultPickupLocationId(stop.location?.id, settings);
};

/**
 * True when the given stop is a default-location pickup AND the workspace has
 * opted to hide such pickups. Intended for single-order display surfaces (e.g.
 * an order's own route timeline) where the caller has already guaranteed a
 * visible sibling exists, so hiding can never make an order un-plannable.
 */
export const isHiddenDefaultPickup = (
  stop: { isPickup?: boolean; location?: { id?: string } | null } | null | undefined,
  settings: WorkspaceSettingsLike,
): boolean => {
  if (!settings?.hidePickupStops) return false;
  return isDefaultLocationPickup(stop, settings);
};
